/* global globalThis */
import React, {createContext, useContext} from "react";
import PropTypes from "prop-types";
import jwt_decode from "jwt-decode";
import {login as doLogin, resendOtp as doResendOtp, verifyOtp as doVerifyOtp} from "../utils/loginApiClient";
import {AppPropertiesUtil, LocalStorageUtil, SessionStorageUtil} from "../utils/localStorageUtil";
import {useLocalStorage, useSessionStorage} from "../utils/useLocalOrSessionStorage";
import {useHistory} from "react-router-dom";
import {getProperties, getSamlResponseforFindHelp, getSamlResponseforUniteUs} from "../actions/constantsActions";
import {getLandingPageURL} from "../utils/util";
import axios from "../utils/apiClient";
import {clientToStateMap} from "../utils/textValueLists";
import {getErrorCode, TRACK_EVENTS, trackPendoEvent} from "../utils/pendoTrack";

const AUTHENTICATION_KEY = "ce-a";
const CE_RESTORE_OBJ = "ce_restore_obj";
const SESSION_TIMEOUT_SHOW_MODAL_MS = 10 * 60 * 1000; // After 10 minutes, Idle Session Modal
const SESSION_TIMEOUT_MODAL_TIMEOUT_MS = 5 * 60 * 1000; // Modal will logout after 5 minutes if not addressed.  Timeout is a total of 15 mintues.
const SESSION_HAS_EXPIRED = "ce-session-expired";
const IMPERSONATION_KEY = "ce-impersonation";

const LOGOUT_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/auth/logout";
const USER_ADMIN_SEARCH_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/user-admin/users/pendo";

const authContext = createContext();
const PENDO_READY_FLAG = "__CE_PENDO_READY";

const AuthProvider = ({ children }) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

const useAuth = () => {
  return useContext(authContext);
};

// Returns the decoded claims the UI should use: impersonation token if present, else primary
function getUiClaims(authentication, impersonationToken) {
  try {
    const impToken = impersonationToken ?? SessionStorageUtil?.getObj?.(IMPERSONATION_KEY) ?? sessionStorage?.getItem?.(IMPERSONATION_KEY);
    const token = impToken || authentication?.access_token || null;

    return token ? jwt_decode(token) : null;
  } catch (e) {
    console.error("Failed to decode JWT for UI claims", e);
    return null;
  }
}

function getFirstAndLastName(name) {
  if (!name || typeof name !== "string") {
    return { firstName: null, lastName: null };
  }

  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) {
    return { firstName: null, lastName: null };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: null };
  }

  return {
    firstName: sanitizeNameValue(parts[0]),
    lastName: sanitizeNameValue(parts.slice(1).join(" "))
  };
}

function sanitizeNameValue(value) {
  const scalar = toStringOrNull(value);
  if (!scalar) {
    return null;
  }

  // Prevent accidental "Smith," / ", John" style values in analytics metadata.
  const noEdgeCommas = scalar.replace(/^,+|,+$/g, "").trim();
  return noEdgeCommas || null;
}

function getResolvedFirstAndLastName(authentication, claims, userRecord) {
  const explicitFirstName = sanitizeNameValue(userRecord?.firstName ?? authentication?.firstName ?? claims?.firstName);
  const explicitLastName = sanitizeNameValue(userRecord?.lastName ?? authentication?.lastName ?? claims?.lastName);
  if (explicitFirstName || explicitLastName) {
    return {
      firstName: explicitFirstName,
      lastName: explicitLastName
    };
  }

  const fullName = toStringOrNull(claims?.name ?? authentication?.name);
  if (!fullName) {
    return { firstName: null, lastName: null };
  }

  // Some payloads provide "Last, First".
  if (fullName.includes(",")) {
    const [lastNamePart, firstNamePart] = fullName.split(",");
    return {
      firstName: sanitizeNameValue(firstNamePart),
      lastName: sanitizeNameValue(lastNamePart)
    };
  }

  return getFirstAndLastName(fullName);
}

function toStringOrNull(value) {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "string") {
    return value.trim() || null;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return null;
}

function resolveAccount(candidate) {
  if (candidate === null || candidate === undefined) {
    return { id: null, name: null };
  }

  const scalar = toStringOrNull(candidate);
  if (scalar) {
    return { id: scalar, name: scalar };
  }

  if (Array.isArray(candidate)) {
    for (const item of candidate) {
      const resolved = resolveAccount(item);
      if (resolved.id) {
        return resolved;
      }
    }
    return { id: null, name: null };
  }

  if (typeof candidate === "object") {
    const id = toStringOrNull(
      candidate.id
      ?? candidate.organizationId
      ?? candidate.orgId
      ?? candidate.teamId
      ?? candidate.value
      ?? candidate.code
    );
    const name = toStringOrNull(
      candidate.accountName
      ?? candidate.name
      ?? candidate.organizationName
      ?? candidate.teamName
      ?? candidate.label
    );
    return { id: id || name, name: name || id };
  }

  return { id: null, name: null };
}

function getInputSourcesCsv(inputSources) {
  if (!Array.isArray(inputSources)) {
    return null;
  }

  const cleaned = inputSources
    .map((item) => {
      const scalar = toStringOrNull(item);
      if (scalar) {
        return scalar;
      }
      const resolved = resolveAccount(item);
      return resolved.id || resolved.name;
    })
    .filter(Boolean);

  return cleaned.length ? cleaned.join(",") : null;
}

function getEnvironmentLabelFromHostname() {
  const hostname = globalThis.window?.location?.hostname;
  switch (hostname) {
    case "qa.careempower.com":
      return "QA";
    case "uat.careempower.com":
      return "UAT";
    case "careempower.com":
    case "www.careempower.com":
      return "Production";
    case "localhost":
      return "Local";
    default:
      return null;
  }
}

function getEnvironmentAccountIdPrefix() {
  const hostname = globalThis.window?.location?.hostname;
  switch (hostname) {
    case "qa.careempower.com":
      return "qa";
    case "uat.careempower.com":
      return "uat";
    case "localhost":
      return "dev";
    case "careempower.com":
    case "www.careempower.com":
      return null;
    default:
      return "dev";
  }
}

function buildPendoAccountId(rawOrganizationId) {
  const orgId = toStringOrNull(rawOrganizationId);
  if (!orgId) {
    return null;
  }

  const prefix = getEnvironmentAccountIdPrefix();
  return prefix ? `${prefix}_${orgId}` : orgId;
}

function getClientMarketSegment(authentication, claims, userRecord) {
  const clientValue =
    userRecord?.client
    ?? userRecord?.clients
    ?? userRecord?.markets
    ?? authentication?.client
    ?? claims?.client
    ?? authentication?.clients
    ?? claims?.clients
    ?? authentication?.marketSegment
    ?? claims?.marketSegment;

  if (Array.isArray(clientValue)) {
    const mapped = clientValue
      .map((item) => {
        const scalar = toStringOrNull(item);
        if (scalar) {
          return clientToStateMap[scalar] || scalar;
        }
        return null;
      })
      .filter(Boolean);
    return mapped.length ? mapped.join(", ") : null;
  }

  const scalar = toStringOrNull(clientValue);
  return scalar ? (clientToStateMap[scalar] || scalar) : null;
}

function normalizeIdentifierValue(value) {
  if (Array.isArray(value)) {
    const cleaned = value.map(toStringOrNull).filter(Boolean);
    return cleaned.length ? cleaned.join(",") : null;
  }
  return toStringOrNull(value);
}

function resolveAccessTypeAndIdentifier(authentication, claims, userRecord) {
  const directAccessType = toStringOrNull(
    userRecord?.accessType
    ?? userRecord?.access_type
    ?? authentication?.accessType
    ?? claims?.accessType
    ?? authentication?.access_type
    ?? claims?.access_type
    ?? authentication?.user?.accessType
    ?? claims?.user?.accessType
  );
  const normalizedAccessType = directAccessType ? directAccessType.toUpperCase() : null;

  const directIdentifier = toStringOrNull(userRecord?.identifier ?? authentication?.identifier ?? claims?.identifier);
  if (directIdentifier && normalizedAccessType) {
    return { accessType: normalizedAccessType, identifier: directIdentifier };
  }

  const identifiers = userRecord?.identifiers ?? authentication?.identifiers ?? claims?.identifiers;
  if (!identifiers || typeof identifiers !== "object") {
    return { accessType: normalizedAccessType, identifier: directIdentifier };
  }

  const keyCandidates = normalizedAccessType
    ? [normalizedAccessType, normalizedAccessType.toLowerCase(), normalizedAccessType.toUpperCase()]
    : [];

  for (const key of keyCandidates) {
    const candidate = normalizeIdentifierValue(identifiers[key]);
    if (candidate) {
      return { accessType: normalizedAccessType || key.toUpperCase(), identifier: candidate };
    }
  }

  for (const [key, rawValue] of Object.entries(identifiers)) {
    const candidate = normalizeIdentifierValue(rawValue);
    if (candidate) {
      return { accessType: (normalizedAccessType || key || "").toUpperCase() || null, identifier: candidate };
    }
  }

  return { accessType: normalizedAccessType, identifier: directIdentifier };
}

function getAnalyticsIdentity(authentication, impersonationAuthentication, userRecord = null) {
  const claims = getUiClaims(authentication, impersonationAuthentication);
  if (!claims) {
    return null;
  }

  const userId = claims.user_id || authentication?.user_id || claims.sub || claims.user_name;
  const email = userRecord?.email || authentication?.email || claims.email || claims.user_name;
  const name = claims.name || authentication?.name;
  const roles = Array.isArray(claims.roles) ? claims.roles : claims.authorities;
  const normalizedRoles = Array.isArray(roles) ? roles : [];
  const { firstName, lastName } = getResolvedFirstAndLastName(authentication, claims, userRecord);
  const { accessType } = resolveAccessTypeAndIdentifier(authentication, claims, userRecord);
  const organizationAccount = resolveAccount(
    userRecord?.organization
    || {
      id: userRecord?.organization?.id,
      name: userRecord?.organization?.name
    }
    || userRecord?.organizationName
    || claims.organization
    || authentication?.organization
    || {
      id: claims.organizationId || claims.orgId || authentication?.organizationId,
      name: claims.organizationName || authentication?.organizationName
    }
  );
  const rawOrganizationId = toStringOrNull(
    userRecord?.organizationTin
    ?? userRecord?.oganizationTin
    ?? userRecord?.organization?.id
    ?? userRecord?.organizationId
    ?? userRecord?.orgId
    ?? claims.organizationId
    ?? claims.orgId
    ?? authentication?.organizationId
    ?? authentication?.orgId
    ?? organizationAccount.id
  );
  const accountId = buildPendoAccountId(rawOrganizationId);
  const accountName = toStringOrNull(userRecord?.organizationName);
  const marketSegment = getClientMarketSegment(authentication, claims, userRecord);

  return {
    claims,
    userId: userId?.toString() || email || "unknown-user",
    email: email || null,
    name: name || null,
    firstName,
    lastName,
    normalizedRoles,
    accessType,
    marketSegment,
    accountId,
    accountName
  };
}

function hasValidPendoIdentity(authentication, identity) {
  const hasClaims = !!getUiClaims(authentication);
  const hasVisitorId = !!identity?.userId;
  const hasAccountId = !!identity?.accountId;
  return hasClaims && hasVisitorId && hasAccountId;
}

function useProvideAuth() {
  // This is to track if the loginIn action is  SSO or actual loggin
  const [authentication, setAuthentication] = useLocalStorage(AUTHENTICATION_KEY);
  const [impersonationAuthentication, setImpersonationAuthentication] = useSessionStorage(IMPERSONATION_KEY);
  const history = useHistory();
  const [properties, setProperties] = React.useState(null);
  const [landingPageOverride, setLandingPageOverride] = React.useState(undefined);
  const propertiesReady = !!properties;
  const [pendoUserRecord, setPendoUserRecord] = React.useState(null);
  const impersonationTrackInitialized = React.useRef(false);
  const pendoInitialized = React.useRef(false);
  const pendoLastIdentitySignature = React.useRef(null);
  const pendoLastOptionsSignature = React.useRef(null);
  const authenticationEmail = authentication?.email;

  React.useEffect(() => {
    let ignore = false;

    async function loadPendoUserRecord() {
      const email = authenticationEmail;
      if (!email) {
        if (!ignore) setPendoUserRecord(null);
        return;
      }

      try {
        const res = await axios.get(USER_ADMIN_SEARCH_URL);
        if (!ignore) {
          const responseData = res?.data;
          let userRow = null;
          if (Array.isArray(responseData?.content)) {
            userRow = responseData.content[0];
          } else if (responseData && typeof responseData === "object") {
            userRow = responseData;
          }
          setPendoUserRecord(userRow || null);
        }
      } catch (e) {
        console.error("Failed to load Pendo user record", e);
        if (!ignore) setPendoUserRecord(null);
      }
    }

    loadPendoUserRecord();
    return () => { ignore = true; };
  }, [authenticationEmail, impersonationAuthentication]);

  // Initialize Pendo once, then identify/update as auth context changes.
  React.useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const MAX_PENDO_INIT_ATTEMPTS = 10;
    const PENDO_INIT_RETRY_DELAY_MS = 500;

    const applyPendoIdentity = () => {
      if (cancelled || attempts > MAX_PENDO_INIT_ATTEMPTS) return; // stop after ~5.5s
      const identity = getAnalyticsIdentity(authentication, impersonationAuthentication, pendoUserRecord);
      const canInitializePendo = hasValidPendoIdentity(authentication, identity);
      const inputSourcesCsv =
        getInputSourcesCsv(
          pendoUserRecord?.inputSources
          ?? pendoUserRecord?.markets
          ?? authentication?.input_sources
        ) || getEnvironmentLabelFromHostname();
      const pendoPayload = canInitializePendo
        ? {
            visitor: {
              // Step 3: map these fields to your canonical user identifiers/traits.
              id: identity.userId,
              email: identity.email,
              firstName: identity.firstName,
              lastName: identity.lastName,
              role: identity.normalizedRoles[0] || null,
              accessType: identity.accessType,
              impersonated: !!impersonationAuthentication,
              isInternalUser: authentication?.is_internal_user ?? identity.claims.is_internal_user ?? null
            },
            account: {
              id: identity.accountId,
              accountName: identity.accountName,
              marketSegment: identity.marketSegment,
              inputSources: inputSourcesCsv
            }
          }
        : null;
      const nextIdentitySignature = JSON.stringify({
        visitorId: pendoPayload?.visitor?.id || null,
        accountId: pendoPayload?.account?.id || null
      });
      const nextOptionsSignature = pendoPayload ? JSON.stringify(pendoPayload) : null;

      const pendo = globalThis.window?.pendo;
      const pendoReady = !!pendo && typeof pendo.initialize === "function";
      if (!pendoReady) {
        attempts += 1;
        setTimeout(applyPendoIdentity, PENDO_INIT_RETRY_DELAY_MS);
        return;
      }

      try {
        if (!canInitializePendo || !pendoPayload) {
          globalThis.window[PENDO_READY_FLAG] = false;
          if (pendoInitialized.current && typeof pendo.clearSession === "function") {
            pendo.clearSession();
            pendoInitialized.current = false;
            pendoLastIdentitySignature.current = null;
            pendoLastOptionsSignature.current = null;
          }
          return;
        }

        if (!pendoInitialized.current) {
          pendo.initialize(pendoPayload);
          pendoInitialized.current = true;
          pendoLastIdentitySignature.current = nextIdentitySignature;
          pendoLastOptionsSignature.current = nextOptionsSignature;
          globalThis.window[PENDO_READY_FLAG] = true;
          return;
        }

        if (pendoLastIdentitySignature.current !== nextIdentitySignature && typeof pendo.identify === "function") {
          pendo.identify(
            pendoPayload?.visitor?.id || null,
            pendoPayload?.account?.id || null
          );
          pendoLastIdentitySignature.current = nextIdentitySignature;
        }

        if (typeof pendo.updateOptions === "function") {
          if (pendoLastOptionsSignature.current !== nextOptionsSignature) {
            pendo.updateOptions(pendoPayload);
            pendoLastOptionsSignature.current = nextOptionsSignature;
          }
        }
        globalThis.window[PENDO_READY_FLAG] = true;
      } catch (e) {
        globalThis.window[PENDO_READY_FLAG] = false;
        console.error("Failed to initialize Pendo", e);
      }
    };

    applyPendoIdentity();
    return () => { cancelled = true; };
  }, [authentication, impersonationAuthentication, pendoUserRecord]);

  React.useEffect(() => {
    if (!impersonationTrackInitialized.current) {
      impersonationTrackInitialized.current = true;
      return;
    }

    if (impersonationAuthentication) {
      trackPendoEvent(TRACK_EVENTS.AUTH_IMPERSONATION_START, {
        route: globalThis.window?.location?.pathname,
        source_module: "authContext"
      });
      return;
    }

    trackPendoEvent(TRACK_EVENTS.AUTH_IMPERSONATION_END, {
      route: globalThis.window?.location?.pathname,
      source_module: "authContext"
    });
  }, [impersonationAuthentication]);

  function isAuthenticated() {
    return !!getUiClaims(authentication, impersonationAuthentication);
  }

  function isAdmin() {
    const claims = getUiClaims(authentication);
    return !!claims?.is_admin;
  }

  function hasAuthority(authority) {
    if (!authority) return true;
    const claims = getUiClaims(authentication);
    return !!claims?.authorities?.includes?.(authority);
  }

  function hasAnyAuthority(authorities = []) {
    return authorities.some((a) => hasAuthority(a));
  }

  function getUsername() {
    const claims = getUiClaims(authentication);
    return claims?.user_name ?? null;
  }

  function getName() {
    const claims = getUiClaims(authentication);
    return claims?.name ?? null;
  }

  function getRoles() {
    const claims = getUiClaims(authentication);
    // fallback to stored roles if claim absent
    return Array.isArray(claims?.roles) ? claims.roles : authentication?.roles || [];
  }

  function getLandingPage(auth) {
    const claims = getUiClaims(auth || authentication);
    if (!auth && landingPageOverride !== undefined) {
      return landingPageOverride;
    }
    return claims?.landing_page ?? null;
  }

  function updateLandingPage(landingPage) {
    setLandingPageOverride(landingPage ?? null);
  }

  const clearAuthAndProperties = () => {
    setAuthentication(null);
    setImpersonationAuthentication(null);
    setLandingPageOverride(undefined);
    AppPropertiesUtil.set(null);
  };

  React.useEffect(() => {
    let ignore = false;
    async function loadProps() {
      if (!authentication) { setProperties(null); return; }
      try {
        const res = await getProperties();
        if (!ignore) {
          setProperties(res.data);
          AppPropertiesUtil.set(res.data); // keep legacy util in sync
        }
      } catch (e) {
        if (!ignore) setProperties(null);
        console.error("Failed to load app properties", e);
      }
    }
    loadProps();
    return () => { ignore = true; };
  }, [authentication]);

  const setAuthAndProperties = async (auth) => {
    setLandingPageOverride(undefined);
    setAuthentication(auth);
  };

  const parseCsv = value => {
    if (!value) {
      return [];
    }
    if (Array.isArray(value)) {
      return value.map((item) => toStringOrNull(item)).filter(Boolean);
    }
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const getOtpChallengePayload = (responseData, username = null) => {
    if (!responseData || typeof responseData !== "object") {
      return null;
    }

    const mfaToken = toStringOrNull(
      responseData.mfa_token
      ?? responseData.mfaToken
      ?? responseData.token
    );

    const sources = parseCsv(responseData.mfa_sources ?? responseData.mfaSources);
    const displayNames = parseCsv(responseData.mfa_displayNames ?? responseData.mfaDisplayNames);

    if (!mfaToken && !sources.length && !displayNames.length) {
      return null;
    }

    const selectedSource = sources[0] || "EMAIL";
    return {
      requiresMfa: true,
      username: username || responseData.username || null,
      mfaToken,
      sources,
      displayNames,
      selectedSource,
      maskedEmail: displayNames[0] || null
    };
  };

  const shouldNavigateToLandingPage = pathname =>
    ["/", "/login", "/verify"].includes(pathname);

  const navigatePostAuth = authData => {
    const restoreObj = LocalStorageUtil.getObj(CE_RESTORE_OBJ);
    if (restoreObj) {
      SessionStorageUtil.setObj(CE_RESTORE_OBJ, restoreObj);
      LocalStorageUtil.setObj(CE_RESTORE_OBJ, null);
      history.push(restoreObj.restoreUrl);
      return;
    }

    if (shouldNavigateToLandingPage(history.location.pathname)) {
      const lp = getLandingPage(authData);
      const lpurl = getLandingPageURL(lp);
      history.push(lpurl);
    }
  };

  const login = async (username, password, termsAccepted = false) => {
    SessionStorageUtil.setObj(CE_RESTORE_OBJ, null);

    const [err, res] = await doLogin(username, password, termsAccepted);
    if (err) {
      const challenge = err?.response?.status === 403
        ? getOtpChallengePayload(err?.response?.data, username)
        : null;
      if (challenge) {
        trackPendoEvent(TRACK_EVENTS.AUTH_OTP_CHALLENGE_SHOWN, {
          route: globalThis.window?.location?.pathname,
          source_module: "authContext.login"
        });
        return challenge;
      }
      trackPendoEvent(TRACK_EVENTS.AUTH_LOGIN_FAILED, {
        route: globalThis.window?.location?.pathname,
        source_module: "authContext.login",
        success: false,
        error_code: getErrorCode(err, "login_failed")
      });
      clearAuthAndProperties();
      return err;
    } else if (res?.data) {
      const challenge = getOtpChallengePayload(res.data, username);
      if (challenge) {
        trackPendoEvent(TRACK_EVENTS.AUTH_OTP_CHALLENGE_SHOWN, {
          route: globalThis.window?.location?.pathname,
          source_module: "authContext.login"
        });
        return challenge;
      }
      await setAuthAndProperties(res.data);
      navigatePostAuth(res.data);
      return true;
    }
  };

  const verifyOtp = async payload => {
    const [err, res] = await doVerifyOtp(payload);
    if (err) {
      trackPendoEvent(TRACK_EVENTS.AUTH_LOGIN_FAILED, {
        route: globalThis.window?.location?.pathname,
        source_module: "authContext.verifyOtp",
        success: false,
        error_code: getErrorCode(err, "otp_verify_failed")
      });
      return err;
    }

    if (!res?.data?.access_token) {
      trackPendoEvent(TRACK_EVENTS.AUTH_LOGIN_FAILED, {
        route: globalThis.window?.location?.pathname,
        source_module: "authContext.verifyOtp",
        success: false,
        error_code: "otp_missing_token"
      });
      return {
        message: "OTP verification response is missing authentication data."
      };
    }

    await setAuthAndProperties(res.data);
    trackPendoEvent(TRACK_EVENTS.AUTH_OTP_SUCCESS, {
      route: globalThis.window?.location?.pathname,
      source_module: "authContext.verifyOtp",
      success: true
    });
    navigatePostAuth(res.data);
    return true;
  };

  const resendOtp = async payload => {
    return doResendOtp(payload);
  };
 
  const loginForRedirect = async (username, password, termsAccepted = false) => {
    SessionStorageUtil.setObj(CE_RESTORE_OBJ, null);
    const [err, res] = await doLogin(username, password, termsAccepted);
    if (err) {
      trackPendoEvent(TRACK_EVENTS.AUTH_LOGIN_FAILED, {
        route: globalThis.window?.location?.pathname,
        source_module: "authContext.loginForRedirect",
        success: false,
        error_code: getErrorCode(err, "login_redirect_failed")
      });
      clearAuthAndProperties();
      return err;
    }
    if (!res?.data) {
      return;
    }

    setAuthentication(res.data);

    let resFromProp = null;
    try {
      resFromProp = await getProperties();
      AppPropertiesUtil.set(resFromProp.data);
    } catch (e) {
      console.error("Error fetching application properties during loginForRedirect:", e);
    }

    // Helper: create and submit a SAML form
    const createAndSubmitSamlForm = (actionUrl, samlResponseValue, relayStateValue) => {
      const myForm = document.createElement('form');
      myForm.setAttribute('action', actionUrl);
      myForm.setAttribute('method', 'post');
      myForm.setAttribute('hidden', 'true');

      const SAMLResponse = document.createElement('input');
      SAMLResponse.setAttribute('type', 'hidden');
      SAMLResponse.setAttribute('name', 'SAMLResponse');
      SAMLResponse.setAttribute('id', 'SAMLResponse');
      SAMLResponse.setAttribute('value', samlResponseValue);

      const RelayState = document.createElement('input');
      RelayState.setAttribute('type', 'hidden');
      RelayState.setAttribute('name', 'RelayState');
      RelayState.setAttribute('id', 'RelayState');
      RelayState.setAttribute('value', relayStateValue);

      myForm.appendChild(SAMLResponse);
      myForm.appendChild(RelayState);
      document.body.appendChild(myForm);
      myForm.submit();
    };

    // Helper: determine source and fetch appropriate SAML response
    const trySamlRedirect = async (props) => {
      if (!props?.data) return;
      const referrer = document?.referrer ?? "";
      const fromUniteUs = referrer.indexOf("uniteus") !== -1;
      const fromFindHelp = referrer.indexOf("findhelp") !== -1;

      let urlKey = null;
      if (fromUniteUs) urlKey = "ce.saml.uniteus.subject-recipient";
      else if (fromFindHelp) urlKey = "ce.saml.findhelp.subject-recipient";

      if (!urlKey) return;

      const url = props.data[urlKey];
      if (!url) return;

      let samlR = null;
      try {
        if (fromUniteUs) {
          samlR = await getSamlResponseforUniteUs();
        } else if (fromFindHelp) {
          samlR = await getSamlResponseforFindHelp();
        }
      } catch (e) {
        console.error("Error fetching SAML response during loginForRedirect:", e);
        return;
      }

      if (samlR?.data && url) {
        createAndSubmitSamlForm(url, samlR.data.samlResponse, samlR.data.relayState);
      }
    };

    await trySamlRedirect(resFromProp);
  };

  const getRefreshToken = () => {
    return authentication ? authentication.refresh_token : null;
  };

  const getAuthToken = () => {
    return authentication ? authentication.access_token : null;
  };

  const logout = () => {
    try {
        SessionStorageUtil.removeObj(IMPERSONATION_KEY);
        exitImpersonation();
        logUserLogoutTime();
    } catch (e) {
      try {
        clearAuthAndProperties();
      } catch (e) {
        console.error("Error clearing auth and properties during logout:", e);
      }
      console.error("Error during logout sequence:", e);
    }
  };

  const logUserLogoutTime = (token) => {
    axios.post(LOGOUT_URL, null, {
      headers: {
        "Content-Type": "application/json"
      },
    })
        .then((response) => {
          clearAuthAndProperties();
          globalThis.window.location = "/";
        })
        .catch((error) => {
          clearAuthAndProperties();
          globalThis.window.location = "/";
        });
  };

  async function exitImpersonation(){
      const targetUsername = getImpersonationTargetUsername();
      const impersonationActive = userImpersonationActive();
      if (impersonationActive) {
          const params = new URLSearchParams();
          params.append('targetUsername', targetUsername);
          params.append('impersonationActive', 'false');
          await axios.post(
              `${process.env.REACT_APP_REST_API_BASE_URL}/v1/impersonate/exit`,
              params
          );
      }

  };

  const getUserId = () => {
    if(impersonationAuthentication) {
        return impersonationAuthentication.user_id;
    }
    return authentication ? authentication.user_id : null;
  };

  const getInputSources = () => {
    if(impersonationAuthentication) {
        return impersonationAuthentication.input_sources;
    }
    return authentication ? authentication.input_sources : null;
  };

  const getEmail = () => {
    if(impersonationAuthentication) {
        return impersonationAuthentication.email;
    }
    return authentication ? authentication.email : null;
  };

  const getEvidenceRequired = () => {
    if(impersonationAuthentication) {
        return impersonationAuthentication.evidenceRequired;
    }
    return authentication?.evidenceRequired ?? false;
  };

  const getInputSource = (idx) => {
    if(impersonationAuthentication) {
        return impersonationAuthentication.input_sources;
    }
    if (!authentication) {
      return null;
    }
    if (!authentication.input_sources) {
      return null;
    }
    return authentication.input_sources[idx];
  };

  const isInternalUser = () => {
    if(impersonationAuthentication) {
        return impersonationAuthentication.is_internal_user;
    }
    return authentication ? authentication.is_internal_user : false;
  }

  const getTeam = () => {
      return impersonationAuthentication ? jwt_decode(impersonationAuthentication).team : authentication?.team;
  }

  const userImpersonationActive = () => {
      return impersonationAuthentication ? jwt_decode(impersonationAuthentication).userImpersonationActive : false;
  };

  const getImpersonatorUsername = () => {
        return impersonationAuthentication ? jwt_decode(impersonationAuthentication).impersonatorUsername : null;
  };

  const getHasImpersonationAccess = () => {
      return impersonationAuthentication ? jwt_decode(impersonationAuthentication).hasImpersonationAccess : authentication.hasImpersonationAccess ?? false;
  };

  const getImpersonationTargetUsername = () => {
    return impersonationAuthentication ? jwt_decode(impersonationAuthentication).user_name : null;
  };

  return {
    isAuthenticated,
    getUsername,
    getName,
    isAdmin,
    hasAuthority,
    getRefreshToken,
    getAuthToken,
    login,
    verifyOtp,
    resendOtp,
    loginForRedirect,
    logout,
    getUserId,
    getInputSources,
    getInputSource,
    getLandingPage,
    updateLandingPage,
    hasAnyAuthority,
    getEmail,
    getEvidenceRequired,
    getRoles,
    isInternalUser,
    getTeam,
    userImpersonationActive,
    getImpersonatorUsername,
    getHasImpersonationAccess,
    getImpersonationTargetUsername,
    setAuthAndProperties,
    properties,
    propertiesReady
  };
}

AuthProvider.propTypes = {
  children: PropTypes.node
};

export {
  AuthProvider,
  useAuth,
  AUTHENTICATION_KEY,
  SESSION_TIMEOUT_SHOW_MODAL_MS,
  SESSION_TIMEOUT_MODAL_TIMEOUT_MS,
  SESSION_HAS_EXPIRED,
  IMPERSONATION_KEY
};
